/**
 * Argus EIP-712 Attestation Approval Queue
 *
 * Admin / hotel-manager surface for reviewing draft attestations,
 * approving or rejecting them, signing them via the backend signer,
 * and watching the L2 dispute window tick down.
 *
 * Backend: /api/argus/attest/* + /api/argus/approver/* + /api/argus/verify/*
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, ShieldAlert, RefreshCw, Clock, Check, X,
  Users, AlertTriangle, FileSignature, ChevronDown, ChevronRight, Copy,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const STATUS_FILTERS = [
  { value: 'pending_approval', label: 'Pending', emoji: '🕐' },
  { value: 'signed_pending_finality', label: 'Signed (in window)', emoji: '✍️' },
  { value: 'final', label: 'Final', emoji: '✅' },
  { value: 'disputed', label: 'Disputed', emoji: '⚠️' },
  { value: 'rejected', label: 'Rejected', emoji: '🚫' },
  { value: 'revoked', label: 'Revoked', emoji: '⛔' },
];

const SUBJECT_COLORS = {
  staff: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  guest: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
};

const STATUS_COLORS = {
  pending_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  signed_pending_finality: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  final: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  disputed: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  revoked: 'bg-rose-500/30 text-rose-200 border-rose-400/40',
  draft: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
};

function timeRemaining(finalityIso) {
  if (!finalityIso) return null;
  const ms = new Date(finalityIso).getTime() - Date.now();
  if (ms <= 0) return 'overdue';
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

function shortHash(h) {
  if (!h) return '';
  return `${h.slice(0, 10)}…${h.slice(-6)}`;
}

export default function ArgusAttestationQueuePage() {
  const nav = useNavigate();
  const [approverId, setApproverId] = useState(() =>
    localStorage.getItem('argus_approver_id') || 'admin@hermes-protocol.com',
  );
  const [approverRole, setApproverRole] = useState(() =>
    localStorage.getItem('argus_approver_role') || 'admin',
  );
  const [status, setStatus] = useState('pending_approval');
  const [hotelId, setHotelId] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [stats, setStats] = useState(null);
  const [approverStats, setApproverStats] = useState(null);
  const [healthInfo, setHealthInfo] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // ─── data load ───────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: '100' });
      if (hotelId.trim()) params.set('hotel_id', hotelId.trim());
      const [listRes, statsRes, healthRes] = await Promise.all([
        fetch(`${API}/api/argus/attest/list?${params}`),
        fetch(`${API}/api/argus/approver/stats`),
        fetch(`${API}/api/argus/attest/health`),
      ]);
      const list = await listRes.json();
      const apv = await statsRes.json();
      const hl = await healthRes.json();
      setItems(list.attestations || []);
      setApproverStats(apv);
      setHealthInfo(hl);
    } catch (e) {
      setItems([]);
    }
    setLoading(false);
  }, [status, hotelId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    localStorage.setItem('argus_approver_id', approverId || '');
    localStorage.setItem('argus_approver_role', approverRole || '');
  }, [approverId, approverRole]);

  // ─── actions ─────────────────────────────────────────────────
  const ensureApprover = async () => {
    try {
      await fetch(`${API}/api/argus/approver/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approver_id: approverId, approver_role: approverRole }),
      });
    } catch {}
  };

  const approve = async (id) => {
    setBusyId(id);
    setActionMsg(null);
    await ensureApprover();
    try {
      const r = await fetch(`${API}/api/argus/attest/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approver_id: approverId,
          approver_role: approverRole,
          notes: 'approved via queue UI',
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'approve failed');
      setActionMsg({ type: 'ok', text: `Approved ${id} → status: ${j.status}` });
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
    setBusyId(null);
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (visible in audit log):');
    if (!reason) return;
    setBusyId(id);
    setActionMsg(null);
    await ensureApprover();
    try {
      const r = await fetch(`${API}/api/argus/attest/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approver_id: approverId,
          approver_role: approverRole,
          reason,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'reject failed');
      setActionMsg({ type: 'ok', text: `Rejected ${id}` });
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
    setBusyId(null);
  };

  const dispute = async (id) => {
    const ev = window.prompt('Evidence (will appear in dispute record):');
    if (!ev) return;
    setBusyId(id);
    try {
      const r = await fetch(`${API}/api/argus/attest/${id}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disputed_by: approverId, evidence: ev }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'dispute failed');
      setActionMsg({ type: 'ok', text: `Filed dispute on ${id}` });
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
    setBusyId(null);
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setActionMsg({ type: 'ok', text: 'copied' }); } catch {}
  };

  // ─── render helpers ─────────────────────────────────────────
  const myApprover = useMemo(() => {
    return (approverStats?.approvers || []).find?.((a) => a.approver_id === approverId);
  }, [approverStats, approverId]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav('/admin')} data-testid="argus-attest-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <FileSignature className="w-6 h-6 text-amber-400" />
                Argus Attestation Queue
              </h1>
              <p className="text-xs text-zinc-400">
                EIP-712 signed proofs · L1 quorum · L2 timelock · L3 audit · L4 staked approvers
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="argus-attest-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Top row: signer + approver + filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 mb-1">Argus signer</div>
              <div className="flex items-center gap-2">
                <Badge className={healthInfo?.signer_configured
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                    : 'bg-rose-500/15 text-rose-300 border-rose-500/30'}>
                  {healthInfo?.signer_configured ? 'configured' : 'NOT configured'}
                </Badge>
                {healthInfo?.signer_address && (
                  <button
                    onClick={() => copy(healthInfo.signer_address)}
                    className="text-xs font-mono text-zinc-300 hover:text-amber-300"
                    data-testid="argus-attest-copy-signer-btn"
                  >
                    {shortHash(healthInfo.signer_address)} <Copy className="inline w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                chain {healthInfo?.domain?.chainId} · timelock {healthInfo?.pipeline?.timelock_hours}h ·
                dual ≥ ${healthInfo?.pipeline?.dual_usd_threshold}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 mb-1">My approver identity</div>
              <input
                type="text"
                value={approverId}
                onChange={(e) => setApproverId(e.target.value)}
                placeholder="approver email or id"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm mb-1"
                data-testid="argus-attest-approver-id-input"
              />
              <select
                value={approverRole}
                onChange={(e) => setApproverRole(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-sm"
                data-testid="argus-attest-approver-role-select"
              >
                <option value="admin">admin</option>
                <option value="owner">owner</option>
                <option value="manager">manager</option>
              </select>
              {myApprover && (
                <div className="mt-2 text-[11px] text-zinc-400 flex items-center gap-2">
                  <Badge className="bg-zinc-800 text-zinc-300">score {myApprover.approver_score}</Badge>
                  <Badge className="bg-zinc-800 text-zinc-300">{myApprover.hermes_staked} HERMES</Badge>
                  <Badge className={myApprover.status === 'active' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}>
                    {myApprover.status}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500 mb-1">Approver pool</div>
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-2xl font-semibold">{approverStats?.active || 0}</div>
                  <div className="text-[10px] text-zinc-500">active</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-amber-300">{approverStats?.frozen || 0}</div>
                  <div className="text-[10px] text-zinc-500">frozen</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-rose-400">{approverStats?.banned || 0}</div>
                  <div className="text-[10px] text-zinc-500">banned</div>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {approverStats?.total_disputed || 0} disputed · {approverStats?.total_approvals || 0} total approvals
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatus(f.value)}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                status === f.value
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-200'
                  : 'border-zinc-800 text-zinc-400 hover:bg-zinc-900'
              }`}
              data-testid={`argus-attest-filter-${f.value}-btn`}
            >
              {f.emoji} {f.label}
            </button>
          ))}
          <input
            type="text"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            placeholder="filter by hotel_id"
            className="ml-2 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm"
            data-testid="argus-attest-hotel-filter-input"
          />
        </div>

        {actionMsg && (
          <div
            className={`mb-3 px-3 py-2 rounded text-sm ${
              actionMsg.type === 'ok'
                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
            }`}
            data-testid="argus-attest-action-msg"
          >
            {actionMsg.text}
          </div>
        )}

        {/* Queue */}
        {loading ? (
          <div className="text-zinc-500 py-12 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="py-10 text-center text-zinc-500">
              No attestations matching <span className="text-zinc-300">{status}</span>.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((a) => (
              <AttestationRow
                key={a.id}
                a={a}
                open={openId === a.id}
                onToggle={() => setOpenId(openId === a.id ? null : a.id)}
                onApprove={() => approve(a.id)}
                onReject={() => reject(a.id)}
                onDispute={() => dispute(a.id)}
                onCopy={copy}
                busy={busyId === a.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttestationRow({ a, open, onToggle, onApprove, onReject, onDispute, onCopy, busy }) {
  const remaining = timeRemaining(a.finality_at);
  const overdue = remaining === 'overdue';
  const remainingApprovals = Math.max(0, (a.required_approvals || 1) - (a.approvals?.length || 0));

  return (
    <Card className={`bg-zinc-900/60 border-zinc-800 ${overdue ? 'ring-1 ring-rose-500/30' : ''}`} data-testid={`argus-attest-row-${a.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button
            onClick={onToggle}
            className="text-zinc-500 hover:text-zinc-200 mt-1"
            data-testid={`argus-attest-toggle-${a.id}-btn`}
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge className={SUBJECT_COLORS[a.subject] || ''}>{a.subject}</Badge>
              <Badge className={STATUS_COLORS[a.status] || ''}>{a.status}</Badge>
              {a.required_approvals > 1 && (
                <Badge className="bg-purple-500/15 text-purple-200 border-purple-500/30">
                  <Users className="w-3 h-3 mr-1" /> dual-approval
                </Badge>
              )}
              {a.critic_review?.verdict === 'flag' && (
                <Badge className="bg-amber-500/15 text-amber-200 border-amber-500/30" title={a.critic_review.reasons?.join('\n')}>
                  <AlertTriangle className="w-3 h-3 mr-1" /> critic flagged
                </Badge>
              )}
              {a.subject === 'guest' && (
                <Badge className={a.subject_consent ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}>
                  {a.subject_consent ? 'consent ✓' : 'no consent'}
                </Badge>
              )}
            </div>
            <div className="text-sm">
              <span className="font-mono text-amber-300">{a.event_type}</span>
              <span className="text-zinc-500 mx-2">·</span>
              <span className="text-zinc-300">{a.subject_id}</span>
              {a.hotel_id && (
                <>
                  <span className="text-zinc-500 mx-2">·</span>
                  <span className="text-zinc-400">{a.hotel_id}</span>
                </>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
              <span>{new Date(a.created_at).toLocaleString()}</span>
              <span>id <button className="font-mono hover:text-amber-300" onClick={() => onCopy(a.id)} data-testid={`argus-attest-copy-id-${a.id}-btn`}>{shortHash(a.id)} <Copy className="inline w-2.5 h-2.5" /></button></span>
              {a.financial_impact_usd ? <span>${a.financial_impact_usd}</span> : null}
              {a.score_impact ? <span>Δ score {a.score_impact > 0 ? '+' : ''}{a.score_impact}</span> : null}
              {a.status === 'signed_pending_finality' && (
                <span className={overdue ? 'text-rose-300' : 'text-blue-300'}>
                  <Clock className="inline w-3 h-3 mr-0.5" />
                  {overdue ? 'overdue (cron will finalise)' : `final in ${remaining}`}
                </span>
              )}
              {remainingApprovals > 0 && a.status === 'pending_approval' && (
                <span className="text-amber-300">{remainingApprovals} approval{remainingApprovals !== 1 ? 's' : ''} needed</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {a.status === 'pending_approval' && (
              <>
                <Button size="sm" onClick={onApprove} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500" data-testid={`argus-attest-approve-${a.id}-btn`}>
                  <Check className="w-4 h-4 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={onReject} disabled={busy} className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid={`argus-attest-reject-${a.id}-btn`}>
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
              </>
            )}
            {a.status === 'signed_pending_finality' && !overdue && (
              <Button size="sm" variant="outline" onClick={onDispute} disabled={busy} className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10" data-testid={`argus-attest-dispute-${a.id}-btn`}>
                <ShieldAlert className="w-4 h-4 mr-1" /> Dispute
              </Button>
            )}
          </div>
        </div>

        {open && (
          <div className="mt-3 pt-3 border-t border-zinc-800/80 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-zinc-500 mb-1">Payload</div>
              <pre className="bg-black/40 p-2 rounded text-zinc-300 max-h-40 overflow-auto">{JSON.stringify(a.payload, null, 2)}</pre>
              <div className="text-zinc-500 mt-2 mb-1">Approvals ({a.approvals?.length || 0}/{a.required_approvals || 1})</div>
              {(a.approvals || []).map((ap, i) => (
                <div key={i} className="text-zinc-300 mb-1">
                  <Badge className="bg-zinc-800 text-zinc-300 mr-1">{ap.approver_role}</Badge>
                  {ap.approver_id} · score {ap.score_at_approval} · {new Date(ap.ts).toLocaleString()}
                </div>
              ))}
              {a.rejection && (
                <div className="text-rose-300 mt-2">
                  Rejected by {a.rejection.rejector_id}: <i>{a.rejection.reason}</i>
                </div>
              )}
            </div>
            <div>
              <div className="text-zinc-500 mb-1">Envelope</div>
              <div className="bg-black/40 p-2 rounded space-y-1">
                <div>payloadHash <span className="font-mono text-amber-300">{shortHash(a.envelope?.payloadHash)}</span></div>
                <div>messageHash <span className="font-mono text-amber-300">{shortHash(a.envelope?.messageHash)}</span></div>
                <div>nonce <span className="font-mono text-zinc-400">{shortHash(a.envelope?.nonce)}</span></div>
                <div>issuedAt {a.envelope?.issuedAt} · expiresAt {a.envelope?.expiresAt}</div>
                {a.signature && (
                  <div className="pt-1">
                    sig <button onClick={() => onCopy(a.signature)} className="font-mono text-emerald-300 hover:text-emerald-200" data-testid={`argus-attest-copy-sig-${a.id}-btn`}>{shortHash(a.signature)} <Copy className="inline w-2.5 h-2.5" /></button>
                  </div>
                )}
                {a.signer_address && (
                  <div>signer <span className="font-mono text-emerald-300">{shortHash(a.signer_address)}</span></div>
                )}
              </div>
              {a.critic_review?.reasons?.length > 0 && (
                <div className="mt-2">
                  <div className="text-zinc-500 mb-1">Critic notes</div>
                  <div className="bg-black/40 p-2 rounded text-zinc-300">
                    <Badge className={a.critic_review.verdict === 'pass' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}>
                      {a.critic_review.verdict}
                    </Badge>
                    <ul className="mt-1 list-disc list-inside">
                      {a.critic_review.reasons.map((r, i) => <li key={i} className="text-[11px]">{r}</li>)}
                    </ul>
                  </div>
                </div>
              )}
              {a.disputes?.length > 0 && (
                <div className="mt-2">
                  <div className="text-zinc-500 mb-1">Disputes</div>
                  {a.disputes.map((d, i) => (
                    <div key={i} className="bg-orange-500/10 border border-orange-500/20 p-2 rounded mb-1">
                      <div className="text-orange-300">{d.disputed_by} · {new Date(d.ts).toLocaleString()}</div>
                      <div className="text-zinc-300">{d.evidence}</div>
                      <Badge className="mt-1 bg-zinc-800 text-zinc-300">{d.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
